import { Dispatch, StateUpdater, useMemo, useState } from 'preact/hooks';
import {
  IcoChevronDown,
  IcoChevronRight,
  IcoFilter,
  IcoGrid,
  IcoList,
  IcoCheckCircle,
  IcoArchive,
  IcoUsers,
  IcoPencil,
  IcoLock,
  IcoEye,
  IcoSelector,
  IcoPlay,
  IcoBook,
  IcoCalendar,
} from '@components/icons';
import { AutoPager } from '../../components/auto-pager';
import { BtnSecondary, Button } from '@components/buttons';
import { Course, Filters } from './types';
import { useCurrentTenant, useCurrentUser } from '@components/router/session-context';
import { SearchBox } from '@components/search-box';
import { EmptyScreen } from './empty-screen';
import { intl, useIntl } from 'shared/intl/use-intl';
import { useLocalStorageState } from 'client/lib/hooks';
import { filterCourses } from './utils';
import { ComponentChildren } from 'preact';
import { partition } from 'shared/utils';
import { Dropdown, MenuItem } from '@components/dropdown';

export type ListType = 'grid' | 'list';

type Props = {
  courses: Course[];
  noBorder?: boolean;
  renderItem: (props: {
    course: Course;
    listType: ListType;
    searchTerm: string;
    displayDate: 'createdAt' | 'lastOpenedAt';
  }) => JSX.Element;
  type: 'courses' | 'bundles' | 'products';
};

const PAGE_SIZE = 20;

function RuzukuPromoLink() {
  const intl = useIntl();
  const tenant = useCurrentTenant();
  const user = useCurrentUser();

  if (user?.level !== 'student' || !tenant.isCore) {
    return null;
  }

  return (
    <div>
      <a
        class="flex flex-col p-6 rounded-lg border hover:border-indigo-600"
        href="https://www.ruzuku.com/?utm_source=student&utm_content=mycourses&utm_medium=link"
      >
        <span class="text-gray-600 mb-2">{intl('Curious about creating your own course?')}</span>
        <span>{intl('Learn more about how Ruzuku can help →')}</span>
      </a>
    </div>
  );
}

function RenderCourses({
  listType,
  courses,
  renderItem,
  searchTerm,
  displayDate,
  children,
}: {
  listType: ListType;
  courses: Course[];
  searchTerm: string;
  renderItem: Props['renderItem'];
  displayDate: 'createdAt' | 'lastOpenedAt';
  children?: ComponentChildren;
}) {
  return (
    <div
      class={
        listType === 'grid'
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'
          : 'flex flex-col gap-4'
      }
    >
      {courses.map((course) =>
        renderItem({
          course,
          searchTerm,
          listType,
          displayDate,
        }),
      )}
      {children}
    </div>
  );
}

function StudentFilters({
  filters,
  setFilters,
  searchTerm,
  setSearchTerm,
  type,
}: {
  filters: Filters;
  setFilters: Dispatch<StateUpdater<Filters>>;
  searchTerm: string;
  setSearchTerm: Dispatch<string>;
  type: Props['type'];
}) {
  return (
    <section>
      <div class="flex items-center">
        <Dropdown
          hideDownIcon
          triggerClass="bg-gray-50 text-gray-600 border border-r-0 border-gray-300 rounded-full rounded-r-none px-4 whitespace-nowrap hidden md:inline-flex items-center gap-2 py-2"
          renderMenu={() => (
            <div class="flex flex-col p-2 pb-0">
              <MenuItem
                onClick={() =>
                  setFilters((s) => ({
                    ...s,
                    sort: 'lastOpenedAt',
                  }))
                }
              >
                {intl('Recently opened by me')}
              </MenuItem>
              <MenuItem
                onClick={() =>
                  setFilters((s) => ({
                    ...s,
                    sort: 'title',
                    sortDirection: -1,
                  }))
                }
              >
                {intl('Title')}
              </MenuItem>
            </div>
          )}
        >
          <IcoFilter />
          {filters.sort === 'title' ? intl('Sort by title') : intl('Sort by recently opened')}
        </Dropdown>
        <SearchBox
          hidePrefix
          class="ruz-input pl-4 text-sm rounded-full md:rounded-l-none"
          onTermChange={(term) => setSearchTerm(term.toLowerCase())}
          placeholder={intl('Find a {model:string}', {
            model:
              type === 'bundles'
                ? intl('bundle')
                : type === 'products'
                  ? intl('product')
                  : intl('course'),
          })}
          value={searchTerm}
        />
      </div>
    </section>
  );
}

function GuideFilters({
  filters,
  setFilters,
  searchTerm,
  setSearchTerm,
  type,
}: {
  filters: Filters;
  setFilters: Dispatch<StateUpdater<Filters>>;
  searchTerm: string;
  setSearchTerm: Dispatch<string>;
  type: Props['type'];
}) {
  const intl = useIntl();
  const hasNonDefaultFilters =
    filters.show !== 'all' ||
    filters.status !== 'all' ||
    filters.type !== 'all' ||
    searchTerm.length > 0;

  function clearFilters() {
    setFilters({
      ...filters,
      show: 'all',
      status: 'all',
      type: 'all',
    });
    setSearchTerm('');
  }

  return (
    <section class="bg-gray-50 p-6 mt-2 rounded-xl flex flex-col gap-6">
      <div class="flex items-center justify-between gap-4">
        <SearchBox
          hidePrefix
          class="ruz-input max-w-md text-sm rounded-md"
          onTermChange={(term) => setSearchTerm(term.toLowerCase())}
          placeholder={
            type === 'bundles'
              ? intl('Search bundles...')
              : type === 'products'
                ? intl('Search products...')
                : intl('Search courses...')
          }
          value={searchTerm}
        />

        {hasNonDefaultFilters && (
          <button
            class="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors whitespace-nowrap"
            onClick={clearFilters}
          >
            {intl('Clear all filters')}
          </button>
        )}
      </div>

      <div class="flex flex-wrap gap-8 items-start">
        <FilterOptions
          label={intl('View')}
          options={filterDefinitions.show.options}
          value={filters.show}
          onChange={(value) => setFilters((s) => ({ ...s, show: value }))}
        />

        {type !== 'bundles' && (
          <FilterOptions
            label={intl('Course Type')}
            options={filterDefinitions.type.options}
            value={filters.type}
            onChange={(value) => setFilters((s) => ({ ...s, type: value }))}
          />
        )}
      </div>

      <div class="flex flex-wrap gap-6 items-end">
        <FilterOptions
          label={intl('Status')}
          options={filterDefinitions.status.options}
          value={filters.status}
          onChange={(value) => setFilters((s) => ({ ...s, status: value }))}
        />
      </div>
    </section>
  );
}

export function CourseList(props: Props) {
  const intl = useIntl();
  const { type, courses } = props;
  const { terminology } = useCurrentTenant();
  const [filters, setFilters] = useState(defaultFilters);
  const [searchTerm, setRawSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const setSearchTerm = (term: string) => {
    setRawSearchTerm(term);
    setPageSize(PAGE_SIZE);
  };
  const [listType, setListType] = useLocalStorageState<ListType>('mycourses.listType', 'grid');
  const currentUser = useCurrentUser()!;
  const isStudentOnly = currentUser.level === 'student';

  const filtered = useMemo(() => {
    return filterCourses({
      courses,
      filters,
      type,
    }).sort((a, b) => {
      const v1 = a[filters.sort];
      const v2 = b[filters.sort];
      return v1 === v2 ? 0 : v1 > v2 ? -filters.sortDirection : filters.sortDirection;
    });
  }, [courses, filters, type, searchTerm]);

  const searched =
    searchTerm.length === 0 ? filtered : filtered.filter((c) => c.search.includes(searchTerm));

  const hasFilters = !areDefaultFilters(filters) || searchTerm.length > 0;
  const visibleCourses = searched.slice(0, pageSize);
  const hasMorePages = visibleCourses.length !== searched.length;
  const [archivedCourses, activeCourses] = partition((x) => x.isArchived, visibleCourses);

  return (
    <>
      <header class={`mb-10 ${props.noBorder ? '' : 'border-b pb-10'}`}>
        <div class="flex justify-between gap-8">
          {!isStudentOnly && (
            <BtnSecondary
              class="rounded-full px-4 bg-gray-50 text-gray-600"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <IcoFilter class="mr-2" />
              {intl('Filters')}
              {isFilterOpen ? <IcoChevronDown class="ml-2" /> : <IcoChevronRight class="ml-2" />}
            </BtnSecondary>
          )}
          {isStudentOnly && (
            <StudentFilters
              filters={filters}
              setFilters={setFilters}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              type={type}
            />
          )}
          <nav class="hidden lg:flex items-center justify-end gap-3">
            {!isStudentOnly && <SortDropdown filters={filters} setFilters={setFilters} />}
            <Button
              data-tooltip="Grid View"
              class={`${
                listType === 'grid' ? 'bg-gray-100' : ''
              } rounded inline-flex h-8 w-8 items-center justify-center relative hover:bg-gray-200`}
              onClick={() => setListType('grid')}
            >
              <span class="flex gap-3 items-center">
                <IcoGrid />
              </span>
            </Button>
            <Button
              data-tooltip="List View"
              class={`${
                listType === 'list' ? 'bg-gray-100' : ''
              } rounded inline-flex h-8 w-8 items-center justify-center relative hover:bg-gray-200`}
              onClick={() => setListType('list')}
            >
              <span class="flex gap-3 items-center">
                <IcoList />
              </span>
            </Button>
          </nav>
        </div>
        {!isStudentOnly && isFilterOpen && (
          <GuideFilters
            filters={filters}
            setFilters={setFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            type={type}
          />
        )}
      </header>
      {!filtered.length && (
        <EmptyScreen
          title={
            type === 'bundles'
              ? intl('No Bundles')
              : type === 'products'
                ? intl('No Products')
                : intl('No Courses')
          }
          subtext={
            isStudentOnly
              ? intl("When you register for {type:string}, they'll show up here.", {
                  type,
                })
              : hasFilters
                ? intl('No {type:string} found with the current filters.', {
                    type,
                  })
                : intl("When you create {type:string}, they'll show up here.", {
                    type,
                  })
          }
        />
      )}
      <section class="flex flex-col gap-10">
        <RenderCourses
          courses={activeCourses}
          listType={listType}
          searchTerm={searchTerm}
          renderItem={props.renderItem}
          displayDate={filters.sort === 'createdAt' ? 'createdAt' : 'lastOpenedAt'}
        >
          {!hasFilters && type === 'courses' && <RuzukuPromoLink />}
        </RenderCourses>

        {!!archivedCourses.length && (
          <>
            <h2 class="font-semibold">Archived {terminology.courses}</h2>
            <RenderCourses
              courses={archivedCourses}
              listType={listType}
              searchTerm={searchTerm}
              renderItem={props.renderItem}
              displayDate={filters.sort === 'createdAt' ? 'createdAt' : 'lastOpenedAt'}
            >
              {!hasFilters && type !== 'bundles' && <RuzukuPromoLink />}
            </RenderCourses>
          </>
        )}
      </section>
      {hasMorePages && <AutoPager onLoadMore={() => setPageSize((p) => p + PAGE_SIZE)} />}
    </>
  );
}

const filterDefinitions = {
  show: {
    options: [
      { title: intl('All'), value: 'all' as const },
      { title: intl('Teaching'), value: 'teaching' as const, icon: <IcoUsers class="w-4 h-4" /> },
      { title: intl('Attending'), value: 'attending' as const, icon: <IcoEye class="w-4 h-4" /> },
      { title: intl('Archived'), value: 'archived' as const, icon: <IcoArchive class="w-4 h-4" /> },
    ],
  },
  status: {
    options: [
      { title: intl('All'), value: 'all' as const },
      { title: intl('Draft'), value: 'draft' as const, icon: <IcoPencil class="w-4 h-4" /> },
      {
        title: intl('Open'),
        value: 'published' as const,
        icon: <IcoCheckCircle class="w-4 h-4" />,
      },
      { title: intl('Closed'), value: 'closed' as const, icon: <IcoLock class="w-4 h-4" /> },
    ],
  },
  type: {
    options: [
      { title: intl('All'), value: 'all' as const },
      {
        title: intl('Drip (Individual)'),
        value: 'ondemand' as const,
        icon: <IcoPlay class="w-4 h-4" />,
      },
      {
        title: intl('Full Access'),
        value: 'openaccess' as const,
        icon: <IcoBook class="w-4 h-4" />,
      },
      {
        title: intl('Drip (Calendar)'),
        value: 'scheduled' as const,
        icon: <IcoCalendar class="w-4 h-4" />,
      },
    ],
  },
};

const sortOptions = [
  { label: intl('Recently Opened'), sortField: 'lastOpenedAt' as const, direction: 1 as const },
  { label: intl('Recently Created'), sortField: 'createdAt' as const, direction: 1 as const },
  { label: intl('Title (A-Z)'), sortField: 'title' as const, direction: -1 as const },
  { label: intl('Title (Z-A)'), sortField: 'title' as const, direction: 1 as const },
  { label: intl('Most Students'), sortField: 'numStudents' as const, direction: 1 as const },
  { label: intl('Fewest Students'), sortField: 'numStudents' as const, direction: -1 as const },
];

const defaultFilters: Filters = {
  sort: 'lastOpenedAt',
  sortDirection: 1,
  status: 'all',
  type: 'all',
  show: 'all',
};

function FilterOptions<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ title: string; value: T; icon?: JSX.Element }>;
  value: T;
  onChange: (value: T) => void;
  label?: string;
}) {
  return (
    <div class="inline-flex flex-col gap-2">
      {label && (
        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
      )}
      <div class="inline-flex bg-gray-100 rounded-lg p-1">
        {options.map((option) => (
          <button
            key={option.value}
            class={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              value === option.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => onChange(option.value)}
          >
            {option.icon}
            {option.title}
          </button>
        ))}
      </div>
    </div>
  );
}

function SortDropdown({
  filters,
  setFilters,
}: {
  filters: Filters;
  setFilters: Dispatch<StateUpdater<Filters>>;
}) {
  const intl = useIntl();
  const currentSort = sortOptions.find(
    (opt) => opt.sortField === filters.sort && opt.direction === filters.sortDirection,
  );

  return (
    <Dropdown
      triggerClass="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
      renderMenu={() => (
        <div class="flex flex-col p-2 pb-0 min-w-[180px]">
          {sortOptions.map((option) => {
            const isActive =
              option.sortField === filters.sort && option.direction === filters.sortDirection;
            return (
              <MenuItem
                key={`${option.sortField}-${option.direction}`}
                onClick={() =>
                  setFilters((s) => ({
                    ...s,
                    sort: option.sortField,
                    sortDirection: option.direction,
                  }))
                }
              >
                <span class="flex items-center gap-2">
                  {isActive && <IcoCheckCircle class="w-4 h-4 text-indigo-600" />}
                  <span class={isActive ? 'font-medium text-indigo-600' : ''}>{option.label}</span>
                </span>
              </MenuItem>
            );
          })}
        </div>
      )}
    >
      <IcoSelector class="w-4 h-4" />
      <span class="font-semibold">{intl('Sort:')}</span>
      <span>{currentSort?.label || intl('Select')}</span>
    </Dropdown>
  );
}

function areDefaultFilters(filters: Filters) {
  return (
    filters.show === defaultFilters.show &&
    filters.sort === defaultFilters.sort &&
    filters.status === defaultFilters.status &&
    filters.type === defaultFilters.type
  );
}
